import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatError } from "@/lib/api";
import { toast } from "sonner";
import { Buildings, Plus, Pencil, Archive, ArrowClockwise, UserPlus, Palette, Users as UsersIcon, Package, ShoppingCart, Mountains, Trash, Key, X } from "@phosphor-icons/react";

const BLANK_ORG = {
  code: "",
  name: "",
  logo_url: "",
  brand_primary: "#002FA7",
  brand_accent: "#FF6A00",
  contact_email: "",
  contact_phone: "",
  address: "",
  preferred_web_address: "",
  domain_option: "luroma_managed",
  domain_notes: "",
  active: true,
};

const BLANK_USER = {
  username: "",
  password: "",
  name: "",
  email: "",
  role: "admin",
};

export default function Organizations() {
  const { user } = useAuth();
  const [orgs, setOrgs] = useState([]);
  const [usage, setUsage] = useState({}); // {org_id -> {users, inventory, purchase_orders, production_tonnes}}
  const [showArchived, setShowArchived] = useState(false);
  const [loading, setLoading] = useState(true);
  const [orgModal, setOrgModal] = useState(null); // {mode: 'create'|'edit', data}
  const [userModal, setUserModal] = useState(null); // {org, data}
  const [drillOrg, setDrillOrg] = useState(null); // cross-tenant drill-down target

  async function load() {
    setLoading(true);
    try {
      const [{ data: rows }, { data: usageRows }] = await Promise.all([
        api.get(`/organizations?include_archived=${showArchived ? "true" : "false"}`),
        api.get("/organizations/usage-summary").catch(() => ({ data: [] })),
      ]);
      setOrgs(Array.isArray(rows) ? rows : []);
      const byId = {};
      (usageRows || []).forEach((u) => { byId[u.org_id] = u; });
      setUsage(byId);
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    // Skip load for tenant admins — page will redirect below.
    if (user && !user.is_provider) return;
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showArchived, user?.is_provider]);

  // LUROMA-only screen. Tenant admins never see this page — the LUROMA
  // control-plane branding must not leak to clients.
  if (user && !user.is_provider) {
    return <Navigate to="/" replace />;
  }

  async function saveOrg() {
    const body = { ...orgModal.data };
    try {
      if (orgModal.mode === "create") {
        await api.post("/organizations", body);
        toast.success("Organization created");
      } else {
        await api.put(`/organizations/${orgModal.data.id}`, body);
        toast.success("Organization updated");
      }
      setOrgModal(null);
      await load();
    } catch (e) {
      toast.error(formatError(e));
    }
  }

  async function archive(o) {
    if (!window.confirm(`Archive ${o.name}? Its data is preserved; you can restore it later.`)) return;
    try {
      await api.delete(`/organizations/${o.id}`);
      toast.success("Organization archived");
      await load();
    } catch (e) { toast.error(formatError(e)); }
  }

  async function restore(o) {
    try {
      await api.post(`/organizations/${o.id}/restore`);
      toast.success("Organization restored");
      await load();
    } catch (e) { toast.error(formatError(e)); }
  }

  async function saveUser() {
    try {
      const payload = { ...userModal.data };
      if (!payload.email) delete payload.email;
      await api.post(`/organizations/${userModal.org.id}/users`, payload);
      toast.success(`Account created for ${userModal.data.username} — they'll be forced to change password on first login.`);
      setUserModal(null);
    } catch (e) {
      toast.error(formatError(e));
    }
  }

  return (
    <div className="space-y-6" data-testid="organizations-page">
      <header className="flex flex-wrap items-center gap-4">
        <div>
          <h1 className="font-heading text-3xl font-black tracking-tighter">LUROMA · Client Setup</h1>
          <p className="text-sm text-[#71717a] mt-1">
            Onboarding intake for every client tenant. Records brand, contact and preferred web address —
            DNS, hosting and billing are handled outside the app.
          </p>
        </div>
        <div className="ml-auto flex items-center gap-3">
          <label className="text-xs flex items-center gap-1.5 text-[#52525b]" data-testid="show-archived-toggle">
            <input
              type="checkbox"
              checked={showArchived}
              onChange={(e) => setShowArchived(e.target.checked)}
            />
            Show archived
          </label>
          <button
            data-testid="new-org-btn"
            className="bg-[#002FA7] text-white px-4 py-2 font-medium flex items-center gap-2 hover:bg-[#001f7a] transition-colors"
            onClick={() => setOrgModal({ mode: "create", data: { ...BLANK_ORG } })}
          >
            <Plus size={16} weight="bold" /> New client
          </button>
        </div>
      </header>

      {loading ? (
        <div className="text-sm phizo-label">Loading client roster…</div>
      ) : orgs.length === 0 ? (
        <div className="border border-dashed border-[#e4e4e7] p-12 text-center text-sm text-[#71717a]">
          No clients yet. Click <b>New client</b> to record the first onboarding intake.
        </div>
      ) : (        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {orgs.map((o) => (
            <div
              key={o.id}
              className={`border p-5 bg-white ${o.active ? "border-[#e4e4e7]" : "border-[#fecaca] bg-[#fef2f2]"}`}
              data-testid={`org-card-${o.code}`}
            >
              <div className="flex items-start gap-3 mb-4">
                <div
                  className="h-12 w-12 flex items-center justify-center rounded font-black text-white"
                  style={{ background: o.brand_primary || "#002FA7" }}
                >
                  {o.logo_url ? (
                    <img src={o.logo_url} alt={o.name} className="h-12 w-12 object-contain" />
                  ) : (
                    o.code?.[0] || "?"
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-heading text-lg font-black leading-tight">{o.name}</div>
                  <div className="phizo-label text-[10px]">{o.code}</div>
                  {!o.active && (
                    <span className="inline-block mt-1 px-2 py-0.5 bg-[#fee2e2] text-[#991b1b] text-[10px] font-bold">
                      ARCHIVED
                    </span>
                  )}
                </div>
                <SetupChecklist org={o} usage={usage[o.id]} testId={o.code} />
              </div>
              <div className="text-xs space-y-1 text-[#52525b] mb-4">
                {o.contact_email && <div>✉ {o.contact_email}</div>}
                {o.contact_phone && <div>☎ {o.contact_phone}</div>}
                {o.address && <div className="text-[10px] text-[#a1a1aa]">{o.address}</div>}
                <div className="flex items-center gap-2 pt-1">
                  <Palette size={12} weight="bold" />
                  <span
                    className="inline-block h-3 w-6 border border-[#e4e4e7]"
                    style={{ background: o.brand_primary }}
                    title={o.brand_primary}
                  />
                  <span
                    className="inline-block h-3 w-6 border border-[#e4e4e7]"
                    style={{ background: o.brand_accent }}
                    title={o.brand_accent}
                  />
                </div>
                {(o.preferred_web_address || o.domain_option) && (
                  <div className="flex items-center gap-2 pt-2 mt-1 border-t border-[#e4e4e7]" data-testid={`org-intake-${o.code}`}>
                    <span className="phizo-label text-[9px] text-[#a1a1aa]">Web</span>
                    <span className="mono text-[11px] text-[#09090b] truncate flex-1" title={o.preferred_web_address}>
                      {o.preferred_web_address || <span className="text-[#a1a1aa]">— not set —</span>}
                    </span>
                    {o.domain_option && (
                      <span
                        className={`text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 whitespace-nowrap ${
                          o.domain_option === "luroma_managed"
                            ? "bg-[#eff6ff] text-[#1e40af]"
                            : "bg-[#ecfeff] text-[#0e7490]"
                        }`}
                        data-testid={`org-domain-badge-${o.code}`}
                      >
                        {o.domain_option === "luroma_managed" ? "LUROMA" : "Client"}
                      </span>
                    )}
                  </div>
                )}
              </div>

              <UsageTiles usage={usage[o.id]} testId={o.code} />
              <div className="flex gap-2 pt-3 border-t border-[#e4e4e7]">
                <button
                  data-testid={`manage-org-${o.code}`}
                  className="flex-1 border border-black bg-black text-white px-2 py-1.5 text-xs font-medium hover:bg-[#27272a] flex items-center justify-center gap-1"
                  onClick={() => setDrillOrg(o)}
                  title="Cross-tenant admin drill-down"
                >
                  <Key size={12} weight="bold" /> Manage
                </button>
                <button
                  data-testid={`edit-org-${o.code}`}
                  className="border border-[#e4e4e7] px-2 py-1.5 text-xs font-medium hover:bg-[#fafafa] flex items-center gap-1"
                  onClick={() => setOrgModal({ mode: "edit", data: { ...o } })}
                >
                  <Pencil size={12} weight="bold" />
                </button>
                <button
                  data-testid={`add-user-${o.code}`}
                  className="border border-[#e4e4e7] px-2 py-1.5 text-xs font-medium hover:bg-[#fafafa] flex items-center gap-1"
                  onClick={() => setUserModal({ org: o, data: { ...BLANK_USER } })}
                >
                  <UserPlus size={12} weight="bold" />
                </button>
                {o.active ? (
                  <button
                    data-testid={`archive-org-${o.code}`}
                    className="border border-[#fca5a5] text-[#991b1b] px-2 py-1.5 text-xs font-medium hover:bg-[#fee2e2] flex items-center gap-1"
                    onClick={() => archive(o)}
                  >
                    <Archive size={12} weight="bold" />
                  </button>
                ) : (
                  <button
                    data-testid={`restore-org-${o.code}`}
                    className="border border-[#86efac] text-[#166534] px-2 py-1.5 text-xs font-medium hover:bg-[#dcfce7] flex items-center gap-1"
                    onClick={() => restore(o)}
                  >
                    <ArrowClockwise size={12} weight="bold" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {orgModal && (
        <OrgModal
          state={orgModal}
          onChange={(patch) => setOrgModal((m) => ({ ...m, data: { ...m.data, ...patch } }))}
          onClose={() => setOrgModal(null)}
          onSave={saveOrg}
        />
      )}
      {userModal && (
        <UserModal
          state={userModal}
          onChange={(patch) => setUserModal((m) => ({ ...m, data: { ...m.data, ...patch } }))}
          onClose={() => setUserModal(null)}
          onSave={saveUser}
        />
      )}

      <MasterOverridePanel currentUser={user} />

      <LurmaTeamPanel currentUserId={user?.id} />

      {drillOrg && (
        <OrgDrillDown
          org={drillOrg}
          onClose={() => setDrillOrg(null)}
          onOrgChanged={(updated) => {
            setOrgs((prev) => prev.map((x) => x.id === updated.id ? { ...x, ...updated } : x));
            setDrillOrg(updated);
          }}
        />
      )}
    </div>
  );
}

function OrgDrillDown({ org: initialOrg, onClose, onOrgChanged }) {
  const [override, setOverride] = React.useState("");
  const [verified, setVerified] = React.useState(false);
  const [verifying, setVerifying] = React.useState(false);
  const [org, setOrg] = React.useState(initialOrg);
  const [users, setUsers] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [editingUser, setEditingUser] = React.useState(null); // user id being edited
  const [userForm, setUserForm] = React.useState({});
  const [orgForm, setOrgForm] = React.useState({ ...initialOrg });
  const [resetResult, setResetResult] = React.useState(null);

  React.useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/luroma/orgs/${initialOrg.id}/users`);
        setOrg(data.org);
        setOrgForm(data.org);
        setUsers(data.users);
      } catch (e) { toast.error(formatError(e)); }
      finally { setLoading(false); }
    })();
  }, [initialOrg.id]);

  const AUTH = { headers: { "X-Override-Code": override } };

  async function verify() {
    setVerifying(true);
    try {
      await api.post("/luroma/verify-override", { code: override });
      setVerified(true);
      toast.success("Master override verified — editing unlocked");
    } catch (e) { toast.error(formatError(e)); }
    finally { setVerifying(false); }
  }

  async function saveOrg() {
    try {
      const { data } = await api.put(`/luroma/orgs/${org.id}`, orgForm, AUTH);
      setOrg(data);
      onOrgChanged(data);
      toast.success("Organisation updated");
    } catch (e) { toast.error(formatError(e)); }
  }

  function startEditUser(u) {
    setEditingUser(u.id);
    setUserForm({ name: u.name, email: u.email || "", role: u.role, password: "" });
  }

  async function saveUser() {
    const body = { name: userForm.name };
    if (userForm.email) body.email = userForm.email;
    if (userForm.role) body.role = userForm.role;
    if (userForm.password) body.password = userForm.password;
    try {
      const { data } = await api.patch(`/luroma/users/${editingUser}`, body, AUTH);
      setUsers((prev) => prev.map((u) => u.id === editingUser ? data : u));
      setEditingUser(null);
      toast.success("User updated");
    } catch (e) { toast.error(formatError(e)); }
  }

  async function resetUserPassword(u) {
    if (!window.confirm(`Reset password for "${u.username}"?`)) return;
    try {
      const { data } = await api.post(`/luroma/users/${u.id}/reset-password`, {}, AUTH);
      setResetResult(data);
      toast.success("Password reset — copy the temporary password below");
    } catch (e) { toast.error(formatError(e)); }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" data-testid="org-drilldown">
      <div className="bg-white w-full max-w-3xl max-h-[92vh] overflow-y-auto shadow-2xl">
        <div className="flex items-start justify-between px-6 py-4 border-b border-[#e4e4e7] sticky top-0 bg-white z-10">
          <div>
            <div className="phizo-label text-[10px] text-[#a1a1aa]">
              LUROMA cross-tenant admin
              {verified && <span className="ml-2 text-[#10B981]">· override unlocked</span>}
            </div>
            <h2 className="font-heading text-2xl font-black tracking-tighter">
              {org.name} <span className="phizo-label text-xs text-[#a1a1aa] mono">{org.code}</span>
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1 text-[#71717a] hover:text-black"
            data-testid="org-drilldown-close"
          >
            <X size={20} weight="bold" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {!verified && (
            <div className="border-2 border-[#f59e0b] bg-[#fffbeb] p-5" data-testid="override-gate">
              <div className="flex items-start gap-3">
                <Key size={24} weight="bold" className="text-[#92400e] mt-1" />
                <div className="flex-1">
                  <h3 className="font-bold text-sm text-[#92400e]">Master override required</h3>
                  <p className="text-xs text-[#92400e] mb-3">
                    Enter the LUROMA master override code to unlock editing controls
                    for this client organisation and its users. Ask the LUROMA super admin
                    if you don't have the code.
                  </p>
                  <div className="flex gap-2">
                    <input
                      type="password"
                      data-testid="override-code-input"
                      value={override}
                      onChange={(e) => setOverride(e.target.value)}
                      className="flex-1 border border-[#e4e4e7] bg-white px-3 py-2 text-sm mono outline-none focus:border-black"
                      placeholder="Enter master override code"
                    />
                    <button
                      type="button"
                      onClick={verify}
                      disabled={verifying || !override}
                      data-testid="override-verify-btn"
                      className="bg-black text-white px-5 py-2 text-sm font-medium disabled:opacity-60"
                    >
                      {verifying ? "Verifying…" : "Unlock"}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Organisation details */}
          <section>
            <div className="phizo-label text-[10px] mb-2">Organisation details</div>
            <div className="grid grid-cols-2 gap-3 border border-[#e4e4e7] p-4">
              <FieldRO label="Code" value={org.code} />
              <label className="block">
                <div className="phizo-label text-[10px] mb-1">Name</div>
                <input
                  data-testid="drill-org-name"
                  value={orgForm.name || ""}
                  onChange={(e) => setOrgForm({ ...orgForm, name: e.target.value })}
                  disabled={!verified}
                  className="w-full border border-[#e4e4e7] bg-white px-3 py-2 text-sm outline-none focus:border-black disabled:bg-[#fafafa] disabled:text-[#a1a1aa]"
                />
              </label>
              <label className="block">
                <div className="phizo-label text-[10px] mb-1">Contact email</div>
                <input
                  value={orgForm.contact_email || ""}
                  onChange={(e) => setOrgForm({ ...orgForm, contact_email: e.target.value })}
                  disabled={!verified}
                  className="w-full border border-[#e4e4e7] bg-white px-3 py-2 text-sm outline-none focus:border-black disabled:bg-[#fafafa]"
                />
              </label>
              <label className="block">
                <div className="phizo-label text-[10px] mb-1">Contact phone</div>
                <input
                  value={orgForm.contact_phone || ""}
                  onChange={(e) => setOrgForm({ ...orgForm, contact_phone: e.target.value })}
                  disabled={!verified}
                  className="w-full border border-[#e4e4e7] bg-white px-3 py-2 text-sm outline-none focus:border-black disabled:bg-[#fafafa]"
                />
              </label>
              <label className="block">
                <div className="phizo-label text-[10px] mb-1">Preferred web address</div>
                <input
                  value={orgForm.preferred_web_address || ""}
                  onChange={(e) => setOrgForm({ ...orgForm, preferred_web_address: e.target.value })}
                  disabled={!verified}
                  className="w-full border border-[#e4e4e7] bg-white px-3 py-2 text-sm outline-none focus:border-black disabled:bg-[#fafafa] mono"
                />
              </label>
              <label className="block">
                <div className="phizo-label text-[10px] mb-1">Domain option</div>
                <select
                  value={orgForm.domain_option || "luroma_managed"}
                  onChange={(e) => setOrgForm({ ...orgForm, domain_option: e.target.value })}
                  disabled={!verified}
                  className="w-full border border-[#e4e4e7] bg-white px-3 py-2 text-sm outline-none focus:border-black disabled:bg-[#fafafa]"
                >
                  <option value="luroma_managed">LUROMA-managed</option>
                  <option value="client_owned">Client-owned</option>
                </select>
              </label>
            </div>
            {verified && (
              <div className="flex justify-end mt-2">
                <button
                  type="button"
                  onClick={saveOrg}
                  data-testid="drill-save-org"
                  className="bg-[#002FA7] text-white px-5 py-2 text-sm font-medium hover:bg-[#001f7a]"
                >
                  Save organisation
                </button>
              </div>
            )}
          </section>

          {/* Users */}
          <section>
            <div className="phizo-label text-[10px] mb-2">
              Users in this organisation ({users.length})
            </div>
            {loading ? (
              <div className="text-xs phizo-label">Loading users…</div>
            ) : (
              <div className="border border-[#e4e4e7]">
                <table className="w-full text-sm">
                  <thead className="bg-[#fafafa] border-b border-[#e4e4e7]">
                    <tr className="phizo-label text-[10px] text-[#71717a]">
                      <th className="text-left px-3 py-2">Username</th>
                      <th className="text-left px-3 py-2">Name</th>
                      <th className="text-left px-3 py-2">Role</th>
                      <th className="text-left px-3 py-2">Email</th>
                      <th className="text-right px-3 py-2"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((u) => (
                      <React.Fragment key={u.id}>
                        <tr className="border-b border-[#e4e4e7] last:border-b-0" data-testid={`drill-user-row-${u.username}`}>
                          <td className="px-3 py-2 mono font-semibold">{u.username}</td>
                          <td className="px-3 py-2">{u.name}</td>
                          <td className="px-3 py-2 text-xs">{u.role}</td>
                          <td className="px-3 py-2 text-xs text-[#71717a]">{u.email || "—"}</td>
                          <td className="px-3 py-2 text-right whitespace-nowrap">
                            {verified && editingUser !== u.id && (
                              <>
                                <button
                                  type="button"
                                  data-testid={`drill-edit-user-${u.username}`}
                                  onClick={() => startEditUser(u)}
                                  className="text-[#71717a] hover:text-black mr-2"
                                  title="Edit user"
                                >
                                  <Pencil size={13} weight="bold" />
                                </button>
                                <button
                                  type="button"
                                  data-testid={`drill-reset-user-${u.username}`}
                                  onClick={() => resetUserPassword(u)}
                                  className="text-[#71717a] hover:text-[#dc2626]"
                                  title="Reset password"
                                >
                                  <Key size={13} weight="bold" />
                                </button>
                              </>
                            )}
                            {!verified && (
                              <span className="text-[10px] text-[#a1a1aa] italic">override required</span>
                            )}
                          </td>
                        </tr>
                        {editingUser === u.id && (
                          <tr className="bg-[#fafafa] border-b border-[#e4e4e7]" data-testid={`drill-edit-form-${u.username}`}>
                            <td colSpan={5} className="px-3 py-3">
                              <div className="grid grid-cols-2 gap-2">
                                <input
                                  data-testid={`drill-edit-name-${u.username}`}
                                  placeholder="Name"
                                  value={userForm.name}
                                  onChange={(e) => setUserForm({ ...userForm, name: e.target.value })}
                                  className="border border-[#e4e4e7] bg-white px-3 py-1.5 text-sm outline-none focus:border-black"
                                />
                                <input
                                  placeholder="Email"
                                  value={userForm.email}
                                  onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
                                  className="border border-[#e4e4e7] bg-white px-3 py-1.5 text-sm outline-none focus:border-black"
                                />
                                <select
                                  data-testid={`drill-edit-role-${u.username}`}
                                  value={userForm.role}
                                  onChange={(e) => setUserForm({ ...userForm, role: e.target.value })}
                                  className="border border-[#e4e4e7] bg-white px-3 py-1.5 text-sm outline-none focus:border-black"
                                >
                                  <option value="admin">Admin</option>
                                  <option value="director">Director</option>
                                  <option value="finance">Finance</option>
                                  <option value="hr">HR</option>
                                  <option value="manager">Manager</option>
                                  <option value="staff">Staff</option>
                                  <option value="viewer">Viewer</option>
                                </select>
                                <input
                                  placeholder="New password (optional)"
                                  value={userForm.password}
                                  onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
                                  className="border border-[#e4e4e7] bg-white px-3 py-1.5 text-sm outline-none focus:border-black"
                                />
                              </div>
                              <div className="flex justify-end gap-2 mt-2">
                                <button
                                  type="button"
                                  onClick={() => setEditingUser(null)}
                                  className="text-xs text-[#71717a] px-3 py-1"
                                >Cancel</button>
                                <button
                                  type="button"
                                  data-testid={`drill-save-user-${u.username}`}
                                  onClick={saveUser}
                                  className="bg-[#002FA7] text-white text-xs font-medium px-4 py-1.5 hover:bg-[#001f7a]"
                                >Save</button>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          {resetResult && (
            <div className="border-2 border-[#10B981] bg-[#f0fdf4] p-4" data-testid="drill-reset-result">
              <div className="phizo-label text-[10px] mb-1">Temporary password issued</div>
              <div className="flex items-center gap-2">
                <span className="mono text-lg font-black tracking-widest select-all">
                  {resetResult.temp_password}
                </span>
                <button
                  type="button"
                  onClick={() => { navigator.clipboard.writeText(resetResult.temp_password); toast.success("Copied"); }}
                  className="text-xs border border-[#10B981] text-[#166534] px-3 py-1 hover:bg-[#dcfce7]"
                >Copy</button>
                <button
                  type="button"
                  onClick={() => setResetResult(null)}
                  className="ml-auto text-xs text-[#71717a] underline"
                >Dismiss</button>
              </div>
              <div className="text-[10px] text-[#166534] mt-1">
                Share via secure channel. {resetResult.username} will be forced to set a new one on sign-in.
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function FieldRO({ label, value }) {
  return (
    <div>
      <div className="phizo-label text-[10px] mb-1">{label}</div>
      <div className="border border-[#e4e4e7] bg-[#fafafa] px-3 py-2 text-sm mono text-[#71717a]">{value}</div>
    </div>
  );
}

function MasterOverridePanel({ currentUser }) {
  const [status, setStatus] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [open, setOpen] = React.useState(false);
  const [form, setForm] = React.useState({ new_code: "", confirm: "", current_password: "" });
  const [showCode, setShowCode] = React.useState(false);
  const [busy, setBusy] = React.useState(false);

  async function load() {
    try {
      const { data } = await api.get("/luroma/master-override/status");
      setStatus(data);
    } catch (e) { /* silent — non-provider = 403 */ }
    setLoading(false);
  }
  React.useEffect(() => { load(); }, []);

  if (loading || !status) return null;

  async function save() {
    if (!form.new_code) return toast.error("Enter a new code.");
    if (form.new_code !== form.confirm) return toast.error("Confirmation does not match.");
    if (!form.current_password) return toast.error("Confirm your password to authorise.");
    setBusy(true);
    try {
      await api.post("/luroma/master-override", {
        new_code: form.new_code,
        current_password: form.current_password,
      });
      toast.success(status.configured ? "Master override code updated." : "Master override code set.");
      setForm({ new_code: "", confirm: "", current_password: "" });
      setOpen(false);
      await load();
    } catch (e) { toast.error(formatError(e)); }
    finally { setBusy(false); }
  }

  const badgeCls = status.configured
    ? "bg-[#dcfce7] text-[#166534]"
    : "bg-[#fee2e2] text-[#991b1b]";
  const badgeText = status.configured ? "Configured" : "Not configured";

  return (
    <section
      className="mt-10 pt-6 border-t-2 border-[#e4e4e7]"
      data-testid="master-override-panel"
    >
      <div className="flex items-start justify-between gap-4 mb-4 flex-wrap">
        <div>
          <div className="phizo-label text-[10px] text-[#a1a1aa]">LUROMA Security</div>
          <h2 className="font-heading text-2xl font-black tracking-tighter">Master override code</h2>
          <p className="text-sm text-[#71717a] mt-1 max-w-xl">
            Required by any LUROMA operator before they can edit users or organisation
            details inside a client tenant. Stored only as a bcrypt hash — never
            displayed. Only the LUROMA super admin ({" "}
            <span className="mono">luroma</span>) can set or change it.
          </p>
        </div>
        <span
          className={`text-[10px] uppercase tracking-wider px-2 py-1 font-bold ${badgeCls}`}
          data-testid="master-override-status-badge"
        >
          {badgeText}
        </span>
      </div>

      <div className="border border-[#e4e4e7] bg-[#fafafa] p-5 flex items-center justify-between flex-wrap gap-4">
        <div className="text-xs text-[#52525b]">
          {status.configured ? (
            <>
              Last updated {status.updated_at ? new Date(status.updated_at).toLocaleString() : "—"}
              {" · by "}<span className="mono">{status.updated_by || "?"}</span>
            </>
          ) : (
            <span className="text-[#991b1b] font-medium">
              Drill-down editing is disabled until the super admin sets a code.
            </span>
          )}
        </div>
        {status.can_set ? (
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            data-testid="master-override-toggle-btn"
            className="border border-black bg-black text-white px-4 py-2 text-sm font-medium flex items-center gap-2 hover:bg-[#27272a]"
          >
            <Key size={14} weight="bold" />
            {open ? "Cancel" : (status.configured ? "Change code" : "Set code")}
          </button>
        ) : (
          <span className="text-[11px] text-[#71717a]">
            Only the LUROMA super admin can change this.
          </span>
        )}
      </div>

      {open && status.can_set && (
        <div
          className="border-l-4 border-[#f59e0b] bg-[#fffbeb] p-5 mt-4"
          data-testid="master-override-form"
        >
          <div className="text-[11px] text-[#92400e] mb-3">
            Choose a strong code (≥ {status.min_length} chars, must include letters and digits).
            Distribute it out-of-band to your tech-support operators. It's stored hashed —
            we cannot recover it if lost, but you can always set a new one.
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="text-xs">
              <div className="phizo-label text-[10px] mb-1">New code</div>
              <div className="flex">
                <input
                  type={showCode ? "text" : "password"}
                  value={form.new_code}
                  onChange={(e) => setForm({ ...form, new_code: e.target.value })}
                  className="flex-1 border border-[#e4e4e7] bg-white px-3 py-2 text-sm mono outline-none focus:border-black"
                  data-testid="master-override-new-code"
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  onClick={() => setShowCode((v) => !v)}
                  className="border border-l-0 border-[#e4e4e7] bg-white px-3 text-xs text-[#71717a] hover:text-black"
                  data-testid="master-override-peek"
                >
                  {showCode ? "hide" : "show"}
                </button>
              </div>
            </label>
            <label className="text-xs">
              <div className="phizo-label text-[10px] mb-1">Confirm new code</div>
              <input
                type={showCode ? "text" : "password"}
                value={form.confirm}
                onChange={(e) => setForm({ ...form, confirm: e.target.value })}
                className="w-full border border-[#e4e4e7] bg-white px-3 py-2 text-sm mono outline-none focus:border-black"
                data-testid="master-override-confirm"
                autoComplete="new-password"
              />
            </label>
            <label className="text-xs sm:col-span-2">
              <div className="phizo-label text-[10px] mb-1">
                Your password (super admin re-auth)
              </div>
              <input
                type="password"
                value={form.current_password}
                onChange={(e) => setForm({ ...form, current_password: e.target.value })}
                className="w-full border border-[#e4e4e7] bg-white px-3 py-2 text-sm outline-none focus:border-black"
                data-testid="master-override-password"
                autoComplete="current-password"
              />
            </label>
          </div>
          <div className="mt-4 flex justify-end">
            <button
              type="button"
              onClick={save}
              disabled={busy}
              data-testid="master-override-save-btn"
              className="bg-[#002FA7] hover:bg-[#001f7a] text-white px-6 py-2 text-sm font-medium disabled:opacity-60"
            >
              {busy ? "Saving…" : (status.configured ? "Change code" : "Save code")}
            </button>
          </div>
        </div>
      )}
    </section>
  );
}

function LurmaTeamPanel({ currentUserId }) {
  const [team, setTeam] = React.useState([]);
  const [busy, setBusy] = React.useState(false);
  const [open, setOpen] = React.useState(false);
  const [form, setForm] = React.useState({ username: "", name: "", email: "", password: "" });

  async function load() {
    try {
      const { data } = await api.get("/luroma/team");
      setTeam(Array.isArray(data) ? data : []);
    } catch (e) { toast.error(formatError(e)); }
  }
  React.useEffect(() => { load(); }, []);

  async function create() {
    if (!form.username || !form.name || !form.password) {
      toast.error("Username, name and password are required.");
      return;
    }
    setBusy(true);
    try {
      const payload = { ...form };
      if (!payload.email) delete payload.email;
      await api.post("/luroma/team", payload);
      toast.success(`Support operator ${form.username} created — they'll change password on first login.`);
      setForm({ username: "", name: "", email: "", password: "" });
      setOpen(false);
      await load();
    } catch (e) { toast.error(formatError(e)); }
    finally { setBusy(false); }
  }

  async function remove(u) {
    if (!window.confirm(`Remove LUROMA operator "${u.username}"?`)) return;
    try {
      await api.delete(`/luroma/team/${u.id}`);
      toast.success("Operator removed");
      await load();
    } catch (e) { toast.error(formatError(e)); }
  }

  return (
    <section
      className="mt-10 pt-6 border-t-2 border-[#e4e4e7]"
      data-testid="luroma-team-panel"
    >
      <div className="flex items-end justify-between gap-4 mb-4">
        <div>
          <div className="phizo-label text-[10px] text-[#a1a1aa]">LUROMA Operators</div>
          <h2 className="font-heading text-2xl font-black tracking-tighter">Tech-support team</h2>
          <p className="text-sm text-[#71717a] mt-1 max-w-lg">
            Provision one login per LUROMA staff member instead of sharing the master account.
            All operators have full provider access to every tenant.
          </p>
        </div>
        <button
          type="button"
          data-testid="luroma-team-add-btn"
          onClick={() => setOpen((v) => !v)}
          className="border border-black bg-black text-white px-4 py-2 text-sm font-medium flex items-center gap-2 hover:bg-[#27272a]"
        >
          <UserPlus size={14} weight="bold" /> {open ? "Cancel" : "Add operator"}
        </button>
      </div>

      {open && (
        <div className="border border-[#e4e4e7] bg-[#fafafa] p-4 mb-4" data-testid="luroma-team-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <label className="block">
              <div className="phizo-label text-[10px] mb-1">Username *</div>
              <input
                data-testid="luroma-team-username"
                value={form.username}
                onChange={(e) => setForm({ ...form, username: e.target.value })}
                className="w-full border border-[#e4e4e7] bg-white px-3 py-2 text-sm outline-none focus:border-black"
                placeholder="alice_support"
              />
            </label>
            <label className="block">
              <div className="phizo-label text-[10px] mb-1">Full name *</div>
              <input
                data-testid="luroma-team-name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full border border-[#e4e4e7] bg-white px-3 py-2 text-sm outline-none focus:border-black"
                placeholder="Alice Banda"
              />
            </label>
            <label className="block">
              <div className="phizo-label text-[10px] mb-1">Email</div>
              <input
                data-testid="luroma-team-email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="w-full border border-[#e4e4e7] bg-white px-3 py-2 text-sm outline-none focus:border-black"
                placeholder="alice@luroma.co.zm"
              />
            </label>
            <label className="block">
              <div className="phizo-label text-[10px] mb-1">Temp password *</div>
              <input
                data-testid="luroma-team-password"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                className="w-full border border-[#e4e4e7] bg-white px-3 py-2 text-sm outline-none focus:border-black"
                placeholder="Min 6 chars"
              />
            </label>
          </div>
          <div className="flex justify-end mt-3">
            <button
              type="button"
              disabled={busy}
              onClick={create}
              data-testid="luroma-team-create"
              className="bg-black text-white px-5 py-2 text-sm font-medium disabled:opacity-60"
            >
              {busy ? "Creating…" : "Create operator"}
            </button>
          </div>
        </div>
      )}

      <div className="border border-[#e4e4e7] bg-white">
        <table className="w-full text-sm">
          <thead className="bg-[#fafafa] border-b border-[#e4e4e7]">
            <tr className="phizo-label text-[10px] text-[#71717a]">
              <th className="text-left px-4 py-2">Username</th>
              <th className="text-left px-4 py-2">Name</th>
              <th className="text-left px-4 py-2">Email</th>
              <th className="text-left px-4 py-2">Created</th>
              <th className="text-right px-4 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {team.map((u) => (
              <tr key={u.id} className="border-b border-[#e4e4e7] last:border-b-0" data-testid={`luroma-team-row-${u.username}`}>
                <td className="px-4 py-3 mono font-semibold flex items-center gap-2">
                  {u.username}
                  {u.must_change_password && (
                    <span
                      title="Must change password on first login"
                      className="text-[10px] bg-[#fef3c7] text-[#92400e] font-bold px-1.5 py-0.5"
                    >
                      NEW
                    </span>
                  )}
                </td>
                <td className="px-4 py-3">{u.name}</td>
                <td className="px-4 py-3 text-xs text-[#71717a]">{u.email || "—"}</td>
                <td className="px-4 py-3 text-xs text-[#71717a] mono">
                  {u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}
                </td>
                <td className="px-4 py-3 text-right">
                  {u.id !== currentUserId && (
                    <button
                      type="button"
                      data-testid={`luroma-team-remove-${u.username}`}
                      onClick={() => remove(u)}
                      className="text-[#a1a1aa] hover:text-[#dc2626]"
                      title="Remove operator"
                    >
                      <Trash size={14} weight="bold" />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function OrgModal({ state, onChange, onClose, onSave }) {
  const { mode, data } = state;
  const create = mode === "create";
  const fileRef = React.useRef(null);
  const [uploading, setUploading] = React.useState(false);

  async function onLogoPick(f) {
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data: res } = await api.post("/upload/logo", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange({ logo_url: res.url });
      toast.success(res.background_removed ? "Logo uploaded — background removed" : "Logo uploaded");
    } catch (e) { toast.error(formatError(e)); }
    finally { setUploading(false); }
  }
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="org-modal">
      <div className="bg-white w-full max-w-lg p-6 space-y-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-start gap-3 pb-4 border-b border-[#e4e4e7]">
          <Buildings size={20} weight="bold" />
          <div>
            <h2 className="font-heading text-xl font-black">{create ? "New organization" : `Edit · ${data.name}`}</h2>
            <p className="text-xs text-[#71717a]">All data for this client is fully isolated. Brand colors drive the login screen and sidebar.</p>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Code" required>
            <input
              data-testid="org-code"
              value={data.code}
              onChange={(e) => onChange({ code: e.target.value.toUpperCase() })}
              className="w-full border border-[#e4e4e7] bg-white px-3 py-2 text-sm focus:border-[#002FA7] focus:ring-2 focus:ring-[#002FA7]/20 outline-none"
              disabled={!create}
              placeholder="ACME"
            />
          </Field>
          <Field label="Name" required>
            <input
              data-testid="org-name"
              value={data.name}
              onChange={(e) => onChange({ name: e.target.value })}
              className="w-full border border-[#e4e4e7] bg-white px-3 py-2 text-sm focus:border-[#002FA7] focus:ring-2 focus:ring-[#002FA7]/20 outline-none"
              placeholder="Acme Mining Ltd"
            />
          </Field>
          <Field label="Contact email" span={2}>
            <input
              data-testid="org-contact-email"
              value={data.contact_email || ""}
              onChange={(e) => onChange({ contact_email: e.target.value })}
              className="w-full border border-[#e4e4e7] bg-white px-3 py-2 text-sm focus:border-[#002FA7] focus:ring-2 focus:ring-[#002FA7]/20 outline-none"
              placeholder="ops@acme.co.zm"
            />
          </Field>
          <Field label="Contact phone">
            <input
              value={data.contact_phone || ""}
              onChange={(e) => onChange({ contact_phone: e.target.value })}
              className="w-full border border-[#e4e4e7] bg-white px-3 py-2 text-sm focus:border-[#002FA7] focus:ring-2 focus:ring-[#002FA7]/20 outline-none"
              placeholder="+260 …"
            />
          </Field>
          <Field label="Logo (auto background-removed)">
            <div className="border border-[#e4e4e7] p-2 flex items-center gap-3">
              <div className="h-14 w-14 border border-[#e4e4e7] bg-[repeating-conic-gradient(#f4f4f5_0deg_90deg,white_90deg_180deg)] bg-[length:12px_12px] flex items-center justify-center">
                {data.logo_url ? (
                  <img src={data.logo_url} alt="Logo" className="max-h-14 max-w-14 object-contain" />
                ) : (
                  <span className="text-[10px] text-[#a1a1aa]">No logo</span>
                )}
              </div>
              <div className="flex-1">
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/*"
                  hidden
                  onChange={(e) => onLogoPick(e.target.files?.[0])}
                  data-testid="org-logo-file"
                />
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  disabled={uploading}
                  data-testid="org-logo-upload-btn"
                  className="text-xs border border-[#e4e4e7] px-3 py-1.5 hover:bg-[#fafafa] disabled:opacity-60"
                >
                  {uploading ? "Uploading…" : data.logo_url ? "Replace logo" : "Upload logo"}
                </button>
                {data.logo_url && (
                  <button
                    type="button"
                    className="text-[10px] text-[#a1a1aa] ml-2 underline"
                    onClick={() => onChange({ logo_url: "" })}
                  >
                    Remove
                  </button>
                )}
                <div className="text-[10px] text-[#a1a1aa] mt-1">PNG, JPG, WebP — solid backgrounds are auto-cleared.</div>
              </div>
            </div>
          </Field>
          <Field label="Address" span={2}>
            <input
              value={data.address || ""}
              onChange={(e) => onChange({ address: e.target.value })}
              className="w-full border border-[#e4e4e7] bg-white px-3 py-2 text-sm focus:border-[#002FA7] focus:ring-2 focus:ring-[#002FA7]/20 outline-none"
              placeholder="Head office address"
            />
          </Field>
          <Field label="Brand primary">
            <input
              data-testid="org-brand-primary"
              type="color"
              value={data.brand_primary || "#002FA7"}
              onChange={(e) => onChange({ brand_primary: e.target.value })}
              className="w-full h-10 border border-[#e4e4e7]"
            />
          </Field>
          <Field label="Brand accent">
            <input
              data-testid="org-brand-accent"
              type="color"
              value={data.brand_accent || "#FF6A00"}
              onChange={(e) => onChange({ brand_accent: e.target.value })}
              className="w-full h-10 border border-[#e4e4e7]"
            />
          </Field>
        </div>

        {/* -------- Web address + domain option (intake record only) -------- */}
        <div className="pt-4 mt-4 border-t border-[#e4e4e7]">
          <div className="flex items-start gap-3 mb-3">
            <div className="phizo-label text-[10px] font-bold text-[#a1a1aa]">Web address (intake — no DNS side-effects)</div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Preferred web address" span={2}>
              <input
                data-testid="org-preferred-web-address"
                value={data.preferred_web_address || ""}
                onChange={(e) => onChange({ preferred_web_address: e.target.value })}
                className="w-full border border-[#e4e4e7] bg-white px-3 py-2 text-sm focus:border-[#002FA7] focus:ring-2 focus:ring-[#002FA7]/20 outline-none mono"
                placeholder="acme.luroma.co.zm or dashboard.acme.co.zm"
              />
              <div className="text-[10px] text-[#a1a1aa] mt-1">
                Just a preference — LUROMA sets up DNS separately.
              </div>
            </Field>
            <Field label="Domain option" span={2}>
              <div className="flex gap-2">
                <button
                  type="button"
                  data-testid="org-domain-luroma"
                  onClick={() => onChange({ domain_option: "luroma_managed" })}
                  className={`flex-1 border px-3 py-2 text-left text-xs transition-colors ${
                    data.domain_option === "luroma_managed"
                      ? "border-[#002FA7] bg-[#eff6ff]"
                      : "border-[#e4e4e7] bg-white hover:border-[#a1a1aa]"
                  }`}
                >
                  <div className="font-bold text-sm">LUROMA-managed</div>
                  <div className="text-[10px] text-[#71717a] mt-0.5">
                    Sub-domain under luroma.co.zm — quickest to spin up.
                  </div>
                </button>
                <button
                  type="button"
                  data-testid="org-domain-client"
                  onClick={() => onChange({ domain_option: "client_owned" })}
                  className={`flex-1 border px-3 py-2 text-left text-xs transition-colors ${
                    data.domain_option === "client_owned"
                      ? "border-[#002FA7] bg-[#eff6ff]"
                      : "border-[#e4e4e7] bg-white hover:border-[#a1a1aa]"
                  }`}
                >
                  <div className="font-bold text-sm">Client-owned</div>
                  <div className="text-[10px] text-[#71717a] mt-0.5">
                    Client provides & controls the domain — LUROMA points to it.
                  </div>
                </button>
              </div>
            </Field>
            <Field label="Domain notes (optional)" span={2}>
              <textarea
                data-testid="org-domain-notes"
                value={data.domain_notes || ""}
                onChange={(e) => onChange({ domain_notes: e.target.value })}
                rows={2}
                className="w-full border border-[#e4e4e7] bg-white px-3 py-2 text-sm focus:border-[#002FA7] focus:ring-2 focus:ring-[#002FA7]/20 outline-none"
                placeholder="Registrar, TTL constraints, planned cut-over date, or anything ops should know."
              />
            </Field>
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-4 border-t border-[#e4e4e7]">
          <button className="px-4 py-2 border border-[#e4e4e7] text-sm" onClick={onClose}>Cancel</button>
          <button
            data-testid="save-org-btn"
            className="px-4 py-2 bg-[#002FA7] text-white text-sm font-medium hover:bg-[#001f7a]"
            onClick={onSave}
          >
            {create ? "Create organization" : "Save changes"}
          </button>
        </div>
      </div>
    </div>
  );
}

function UserModal({ state, onChange, onClose, onSave }) {
  const { org, data } = state;
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="user-modal">
      <div className="bg-white w-full max-w-md p-6 space-y-4">
        <div className="pb-4 border-b border-[#e4e4e7]">
          <h2 className="font-heading text-xl font-black">New account · {org.name}</h2>
          <p className="text-xs text-[#71717a]">User will be forced to change password on first login.</p>
        </div>
        <div className="grid grid-cols-1 gap-3">
          <Field label="Full name" required>
            <input data-testid="new-user-name" value={data.name} onChange={(e) => onChange({ name: e.target.value })} className="w-full border border-[#e4e4e7] bg-white px-3 py-2 text-sm focus:border-[#002FA7] focus:ring-2 focus:ring-[#002FA7]/20 outline-none" />
          </Field>
          <Field label="Username" required>
            <input data-testid="new-user-username" value={data.username} onChange={(e) => onChange({ username: e.target.value })} className="w-full border border-[#e4e4e7] bg-white px-3 py-2 text-sm focus:border-[#002FA7] focus:ring-2 focus:ring-[#002FA7]/20 outline-none" />
          </Field>
          <Field label="Email">
            <input data-testid="new-user-email" value={data.email} onChange={(e) => onChange({ email: e.target.value })} className="w-full border border-[#e4e4e7] bg-white px-3 py-2 text-sm focus:border-[#002FA7] focus:ring-2 focus:ring-[#002FA7]/20 outline-none" placeholder="optional" />
          </Field>
          <Field label="Temporary password" required>
            <input data-testid="new-user-password" value={data.password} onChange={(e) => onChange({ password: e.target.value })} className="w-full border border-[#e4e4e7] bg-white px-3 py-2 text-sm focus:border-[#002FA7] focus:ring-2 focus:ring-[#002FA7]/20 outline-none" placeholder="Min 6 chars" />
          </Field>
          <Field label="Role">
            <select data-testid="new-user-role" value={data.role} onChange={(e) => onChange({ role: e.target.value })} className="w-full border border-[#e4e4e7] bg-white px-3 py-2 text-sm focus:border-[#002FA7] focus:ring-2 focus:ring-[#002FA7]/20 outline-none">
              <option value="admin">Admin</option>
              <option value="director">Director</option>
              <option value="finance">Finance</option>
              <option value="hr">HR</option>
              <option value="manager">Manager</option>
              <option value="staff">Staff</option>
              <option value="viewer">Viewer</option>
            </select>
          </Field>
        </div>
        <div className="flex justify-end gap-2 pt-4 border-t border-[#e4e4e7]">
          <button className="px-4 py-2 border border-[#e4e4e7] text-sm" onClick={onClose}>Cancel</button>
          <button
            data-testid="save-user-btn"
            className="px-4 py-2 bg-[#002FA7] text-white text-sm font-medium hover:bg-[#001f7a]"
            onClick={onSave}
          >Create account</button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, required, children, span = 1 }) {
  return (
    <label className={`block col-span-${span}`}>
      <div className="phizo-label text-[10px] mb-1">
        {label}{required && <span className="text-red-500 ml-0.5">*</span>}
      </div>
      {children}
    </label>
  );
}

function UsageTiles({ usage, testId }) {
  const tiles = [
    { icon: UsersIcon, label: "Users", value: usage?.users ?? "—" },
    { icon: Package, label: "SKUs", value: usage?.inventory ?? "—" },
    { icon: ShoppingCart, label: "POs", value: usage?.purchase_orders ?? "—" },
    {
      icon: Mountains,
      label: "Tonnes",
      value: usage?.production_tonnes != null
        ? formatCompact(usage.production_tonnes)
        : "—",
    },
  ];
  const silent = usage && (usage.users || 0) <= 1
    && (usage.inventory || 0) === 0
    && (usage.purchase_orders || 0) === 0
    && (usage.production_tonnes || 0) === 0;
  return (
    <div className="mb-4">
      {silent && (
        <div
          className="mb-2 flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-[#c2410c] bg-[#ffedd5] px-2 py-1"
          data-testid={`org-silent-${testId}`}
        >
          <span className="inline-block h-1.5 w-1.5 bg-[#c2410c] rounded-full animate-pulse" />
          Silent · never used
        </div>
      )}
      <div className="grid grid-cols-4 gap-1" data-testid={`org-usage-${testId}`}>
        {tiles.map((t) => {
          const Icon = t.icon;
          return (
            <div key={t.label} className="border border-[#e4e4e7] p-2 text-center bg-[#fafafa]">
              <Icon size={12} weight="bold" className="text-[#71717a] mx-auto mb-0.5" />
              <div className="font-heading text-sm font-black leading-tight">{t.value}</div>
              <div className="phizo-label text-[8px] text-[#a1a1aa] mt-0.5">{t.label}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function formatCompact(n) {
  const v = Number(n) || 0;
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000) return `${(v / 1_000).toFixed(1)}k`;
  return v.toLocaleString(undefined, { maximumFractionDigits: 0 });
}

// Progress ring + checklist popover: brand / contact / web / admin / import.
// Renders in the top-right of each Client-Setup card so LUROMA reps can
// eyeball at a glance which intakes are still half-baked.
function SetupChecklist({ org, usage, testId }) {
  const [open, setOpen] = React.useState(false);
  const items = React.useMemo(() => [
    {
      key: "brand",
      label: "Brand",
      done: !!(org.logo_url
        || (org.brand_primary && org.brand_primary.toLowerCase() !== "#002fa7")
        || (org.brand_accent && org.brand_accent.toLowerCase() !== "#ff6a00")),
      hint: "Upload a logo or set custom brand colours.",
    },
    {
      key: "contact",
      label: "Contact",
      done: !!(org.contact_email || org.contact_phone),
      hint: "Add at least an email or phone for the client contact.",
    },
    {
      key: "web",
      label: "Web address",
      done: !!org.preferred_web_address,
      hint: "Record the preferred sub-domain (LUROMA-managed or client-owned).",
    },
    {
      key: "admin",
      label: "First admin",
      done: !!(usage && usage.has_admin),
      hint: "Provision at least one admin/director user for the tenant.",
    },
    {
      key: "import",
      label: "First import",
      done: !!(usage && ((usage.inventory || 0) + (usage.employees || 0) > 0)),
      hint: "Seed the control room with inventory or employees so it feels lived-in.",
    },
  ], [org, usage]);

  const total = items.length;
  const done = items.filter((i) => i.done).length;
  const pct = Math.round((done / total) * 100);
  const complete = done === total;

  // SVG ring geometry
  const size = 44;
  const stroke = 4;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const dash = (done / total) * c;
  const ringColour = complete ? "#10B981" : done === 0 ? "#e4e4e7" : org.brand_primary || "#002FA7";

  return (
    <div className="relative" data-testid={`org-checklist-${testId}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="block hover:scale-105 transition-transform"
        title={`Setup ${done} of ${total} · ${pct}%`}
        data-testid={`org-checklist-btn-${testId}`}
      >
        <svg width={size} height={size} className="block -rotate-90">
          <circle cx={size / 2} cy={size / 2} r={r}
            stroke="#f4f4f5" strokeWidth={stroke} fill="none" />
          <circle cx={size / 2} cy={size / 2} r={r}
            stroke={ringColour} strokeWidth={stroke} fill="none"
            strokeDasharray={`${dash} ${c - dash}`}
            strokeLinecap="round"
            style={{ transition: "stroke-dasharray 0.4s ease" }}
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <span className={`font-heading font-black text-[11px] leading-none ${complete ? "text-[#10B981]" : "text-[#09090b]"}`}>
            {done}/{total}
          </span>
        </div>
      </button>
      {open && (
        <div
          className="absolute right-0 top-full mt-1 z-20 w-64 bg-white border border-[#e4e4e7] shadow-lg p-3"
          onMouseLeave={() => setOpen(false)}
          data-testid={`org-checklist-pop-${testId}`}
        >
          <div className="flex items-center justify-between pb-2 border-b border-[#e4e4e7] mb-2">
            <div className="phizo-label text-[10px]">Setup checklist</div>
            <div className="text-[10px] font-bold" style={{ color: complete ? "#10B981" : "#71717a" }}>
              {complete ? "✓ Ready" : `${done} of ${total}`}
            </div>
          </div>
          <ul className="space-y-1.5">
            {items.map((it) => (
              <li key={it.key} className="flex items-start gap-2 text-xs">
                <span
                  className={`inline-flex h-4 w-4 items-center justify-center rounded-full flex-shrink-0 mt-0.5 text-[10px] font-bold ${
                    it.done ? "bg-[#10B981] text-white" : "bg-[#f4f4f5] text-[#a1a1aa] border border-[#e4e4e7]"
                  }`}
                  data-testid={`checklist-${testId}-${it.key}${it.done ? "-done" : ""}`}
                >
                  {it.done ? "✓" : ""}
                </span>
                <div className="flex-1 min-w-0">
                  <div className={`font-semibold text-[11px] ${it.done ? "text-[#166534]" : "text-[#09090b]"}`}>
                    {it.label}
                  </div>
                  {!it.done && (
                    <div className="text-[10px] text-[#71717a] leading-snug">{it.hint}</div>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}